import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

// Global interceptor: convert scary network errors to friendly messages
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.code === 'ERR_NETWORK' || !error.response) {
      error.friendlyMessage = 'Could not reach the server. Please try again.';
    } else if (error.response?.status >= 500) {
      error.friendlyMessage = 'Something went wrong on our end. Please try again in a moment.';
    }
    return Promise.reject(error);
  }
);

function getHeaders() {
  const token = localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const api = {
  register: (data) => axios.post(`${BASE}/auth/register`, data, { withCredentials: true }),
  registerDirect: (data) => axios.post(`${BASE}/auth/register-direct`, data, { withCredentials: true }),
  login: (data) => axios.post(`${BASE}/auth/login`, data, { withCredentials: true }),
  sendOtp: (data) => axios.post(`${BASE}/auth/send-otp`, data),
  verifyOtp: (data) => axios.post(`${BASE}/auth/verify-otp`, data, { withCredentials: true }),
  processOAuthSession: (sessionId) =>
    axios.get(`${BASE}/auth/session?session_id=${sessionId}`, { withCredentials: true }),
  logout: () => axios.post(`${BASE}/auth/logout`, {}, {
    headers: getHeaders(), withCredentials: true
  }),
  getMe: () => axios.get(`${BASE}/auth/me`, { headers: getHeaders(), withCredentials: true }),
  updateProfile: (data) => axios.put(`${BASE}/user/profile`, data, {
    headers: getHeaders(), withCredentials: true
  }),
  parsePDF: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${BASE}/parse-pdf`, fd, {
      headers: getHeaders(),
      withCredentials: true
    });
  },
  generate: (data) => axios.post(`${BASE}/generate`, data, {
    headers: getHeaders(), withCredentials: true,
    timeout: 15000
  }),
  generateStatus: (taskId) => axios.get(`${BASE}/generate/status/${taskId}`, {
    headers: getHeaders(), withCredentials: true,
    timeout: 10000
  }),
  getHistory: () => axios.get(`${BASE}/history`, { headers: getHeaders(), withCredentials: true }),
  deleteHistory: (id) => axios.delete(`${BASE}/history/${id}`, {
    headers: getHeaders(), withCredentials: true
  }),
  clearHistory: () => axios.delete(`${BASE}/history/all`, {
    headers: getHeaders(), withCredentials: true
  }),
  getUsage: () => axios.get(`${BASE}/usage`, { headers: getHeaders(), withCredentials: true }),

  // Resume library
  saveResume: (data) => axios.post(`${BASE}/resume-library`, data, { headers: getHeaders(), withCredentials: true }),
  getResumeLibrary: () => axios.get(`${BASE}/resume-library`, { headers: getHeaders(), withCredentials: true }),
  getSavedResume: (id) => axios.get(`${BASE}/resume-library/${id}`, { headers: getHeaders(), withCredentials: true }),
  deleteResume: (id) => axios.delete(`${BASE}/resume-library/${id}`, { headers: getHeaders(), withCredentials: true }),

  // Admin
  adminOverview: () => axios.get(`${BASE}/admin/overview`, { headers: getHeaders(), withCredentials: true }),
  adminGetUsers: () => axios.get(`${BASE}/admin/users`, { headers: getHeaders(), withCredentials: true }),
  adminCreateUser: (data) => axios.post(`${BASE}/admin/users`, data, { headers: getHeaders(), withCredentials: true }),
  adminUpdateUser: (id, data) => axios.put(`${BASE}/admin/users/${id}`, data, { headers: getHeaders(), withCredentials: true }),
  adminDeleteUser: (id) => axios.delete(`${BASE}/admin/users/${id}`, { headers: getHeaders(), withCredentials: true }),
  adminVisitors: (days = 30) => axios.get(`${BASE}/admin/analytics/visitors?days=${days}`, { headers: getHeaders(), withCredentials: true }),
  adminCountries: () => axios.get(`${BASE}/admin/analytics/countries`, { headers: getHeaders(), withCredentials: true }),
  adminGetPayments: () => axios.get(`${BASE}/admin/payments`, { headers: getHeaders(), withCredentials: true }),
  adminAddPayment: (data) => axios.post(`${BASE}/admin/payments`, data, { headers: getHeaders(), withCredentials: true }),
  adminDeletePayment: (id) => axios.delete(`${BASE}/admin/payments/${id}`, { headers: getHeaders(), withCredentials: true }),
  adminVerifyPayment: (id, action) => axios.put(`${BASE}/admin/payments/${id}/verify`, { action }, { headers: getHeaders(), withCredentials: true }),

  // User payments
  submitPayment: (data) => axios.post(`${BASE}/payments/submit`, data, { headers: getHeaders(), withCredentials: true }),
  razorpayCreateOrder: (data) => axios.post(`${BASE}/razorpay/create-order`, data, { headers: getHeaders(), withCredentials: true }),
  razorpayCreateOrderCoupon: (data) => axios.post(`${BASE}/razorpay/create-order-coupon`, data, { headers: getHeaders(), withCredentials: true }),
  razorpayVerify: (data) => axios.post(`${BASE}/razorpay/verify`, data, { headers: getHeaders(), withCredentials: true }),

  // Coupons
  validateCoupon: (data) => axios.post(`${BASE}/coupons/validate`, data, { headers: getHeaders(), withCredentials: true }),
  applyCoupon: (data) => axios.post(`${BASE}/coupons/apply`, data, { headers: getHeaders(), withCredentials: true }),

  // Admin coupons
  getAdminCoupons: () => axios.get(`${BASE}/admin/coupons`, { headers: getHeaders(), withCredentials: true }),
  createAdminCoupon: (data) => axios.post(`${BASE}/admin/coupons`, data, { headers: getHeaders(), withCredentials: true }),
  deleteAdminCoupon: (id) => axios.delete(`${BASE}/admin/coupons/${id}`, { headers: getHeaders(), withCredentials: true }),

  // Job Tracker (Career plan)
  getJobs: () => axios.get(`${BASE}/job-tracker`, { headers: getHeaders(), withCredentials: true }),
  createJob: (data) => axios.post(`${BASE}/job-tracker`, data, { headers: getHeaders(), withCredentials: true }),
  updateJob: (id, data) => axios.put(`${BASE}/job-tracker/${id}`, data, { headers: getHeaders(), withCredentials: true }),
  deleteJob: (id) => axios.delete(`${BASE}/job-tracker/${id}`, { headers: getHeaders(), withCredentials: true }),

  // Career Tools (Career plan)
  generateColdEmail: (data) => axios.post(`${BASE}/career/cold-email`, data, { headers: getHeaders(), withCredentials: true }),
  bulkGenerate: (data) => axios.post(`${BASE}/career/bulk-generate`, data, { headers: getHeaders(), withCredentials: true }),
  bulkStatus: (batchId) => axios.get(`${BASE}/career/bulk-status/${batchId}`, { headers: getHeaders(), withCredentials: true }),

  // Free Tools (no auth)
  atsCheck: (data) => axios.post(`${BASE}/tools/ats-check`, data),

  // Admin Notifications
  getNotifications: () => axios.get(`${BASE}/admin/notifications`, { headers: getHeaders(), withCredentials: true }),
  markNotificationsRead: () => axios.post(`${BASE}/admin/notifications/mark-read`, {}, { headers: getHeaders(), withCredentials: true }),

  // Advanced Analytics
  adminAdvancedAnalytics: () => axios.get(`${BASE}/admin/analytics/advanced`, { headers: getHeaders(), withCredentials: true }),

  // Score History
  getScoreHistory: () => axios.get(`${BASE}/score-history`, { headers: getHeaders(), withCredentials: true }),

  // Referral
  getMyReferralCode: () => axios.get(`${BASE}/referral/my-code`, { headers: getHeaders(), withCredentials: true }),
  applyReferral: (data) => axios.post(`${BASE}/referral/apply`, data, { headers: getHeaders(), withCredentials: true }),
};
